import { useState } from 'react';
import { Modal, ModalBody, ModalFooter } from '../ui/Modal';
import { useUpdateComplaintStatus } from '../../hooks';
import type { ComplaintStatus } from '../../api';
import { AlertCircle, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

interface StatusUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  complaintId: string;
  currentStatus: ComplaintStatus;
}

// Valid status transitions
const VALID_TRANSITIONS: Record<ComplaintStatus, ComplaintStatus[]> = {
  submitted: ['under_review'],
  under_review: ['approved', 'rejected'],
  approved: [],
  rejected: [],
};

const statusConfig: Record<ComplaintStatus, { label: string; color: string; icon: React.ReactNode }> = {
  submitted: {
    label: 'Submitted',
    color: 'bg-gray-100 text-gray-700',
    icon: null,
  },
  under_review: {
    label: 'Under Review',
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    icon: <AlertCircle className="w-4 h-4" />,
  },
  approved: {
    label: 'Approved',
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: <CheckCircle2 className="w-4 h-4" />,
  },
  rejected: {
    label: 'Rejected',
    color: 'bg-red-100 text-red-700 border-red-200',
    icon: <XCircle className="w-4 h-4" />,
  },
};

export function StatusUpdateModal({
  isOpen,
  onClose,
  complaintId,
  currentStatus,
}: StatusUpdateModalProps) {
  const [selectedStatus, setSelectedStatus] = useState<ComplaintStatus | ''>('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const updateStatusMutation = useUpdateComplaintStatus(complaintId);

  const validTransitions = VALID_TRANSITIONS[currentStatus];
  const requiresComment = selectedStatus === 'approved' || selectedStatus === 'rejected';

  const handleSubmit = async () => {
    if (!selectedStatus) {
      setError('Please select a status');
      return;
    }

    if (requiresComment && !comment.trim()) {
      setError('Comment is required when approving or rejecting');
      return;
    }

    setError(null);

    try {
      await updateStatusMutation.mutateAsync({
        status: selectedStatus,
        comment: comment.trim() || undefined,
      });
      // Reset and close on success
      setSelectedStatus('');
      setComment('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const handleClose = () => {
    if (!updateStatusMutation.isPending) {
      setSelectedStatus('');
      setComment('');
      setError(null);
      onClose();
    }
  };

  if (validTransitions.length === 0) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Update Status">
        <ModalBody>
          <p className="text-gray-600">
            This complaint has reached a terminal status and cannot be updated further.
          </p>
        </ModalBody>
        <ModalFooter>
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Update Complaint Status">
      <ModalBody>
        <div className="space-y-4">
          {/* Current status display */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Status</label>
            <div
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${statusConfig[currentStatus].color}`}
            >
              {statusConfig[currentStatus].icon}
              {statusConfig[currentStatus].label}
            </div>
          </div>

          {/* New status selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select New Status <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {validTransitions.map((status) => (
                <label
                  key={status}
                  data-testid={`modal-status-option-${status}`}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedStatus === status
                      ? 'border-gray-900 bg-gray-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="status"
                    value={status}
                    checked={selectedStatus === status}
                    onChange={(e) => setSelectedStatus(e.target.value as ComplaintStatus)}
                    className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-gray-300"
                  />
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig[status].color}`}
                  >
                    {statusConfig[status].icon}
                    {statusConfig[status].label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Comment field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Comment {requiresComment && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={
                requiresComment
                  ? 'Please provide a reason for your decision...'
                  : 'Optional comment...'
              }
              rows={4}
              data-testid="modal-status-update-input-comment"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent"
            />
            {requiresComment && (
              <p className="mt-1 text-xs text-gray-500">
                A comment is required when approving or rejecting a complaint
              </p>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <button
          onClick={handleClose}
          disabled={updateStatusMutation.isPending}
          data-testid="modal-status-update-btn-cancel"
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={updateStatusMutation.isPending || !selectedStatus}
          data-testid="modal-status-update-btn-submit"
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {updateStatusMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          {updateStatusMutation.isPending ? 'Updating...' : 'Update Status'}
        </button>
      </ModalFooter>
    </Modal>
  );
}
